import React, { useState, useEffect, useRef } from "react";
// import styles from "../DynamicForm.module.scss";
import { createSerialKey } from "../utils/formHelpers";

/**
 * Component to count selected options and validate against min/max requirements
 * Provides real-time validation for form fields
 */
const SelectionCounter = ({ children, step, onSelectionChange, showValidationError }) => {
	const [selectedCount, setSelectedCount] = useState(0);
	const [internalValidation, setInternalValidation] = useState(true);
	const [showRealTimeValidation, setShowRealTimeValidation] = useState(showValidationError || false);
	const isInitialRender = useRef(true);
	const prevSelectedCount = useRef(0);
	const filledTextInputs = useRef(0);
	const userInteracted = useRef(false);

	// Determine min and max count values
	const minCount = step?.min_count || 0;
	const maxCount = step?.max_count || Infinity;


	// Reference to the container element
	const containerRef = useRef(null);
	
	// Step number for identification (from props, or fallback to stepNumber directly in step)
	const stepNumber = step?.stepNumber || step?.step_number || 1;

	useEffect(() => {
		if (showValidationError && !showRealTimeValidation ){
			setShowRealTimeValidation(true);
		}
	},[showValidationError, showRealTimeValidation]);	
	
	
	useEffect(() => {
		if (containerRef?.current) {
			containerRef.current.dispatchEvent(
				new CustomEvent("validity-changed", { 
					bubbles: true,
					detail: {
						validity: internalValidation,
						step: step
				  }, })
			  );
			  
		}

	}, [internalValidation, containerRef, step]);

	// Effect to count selections and text inputs on mount and when changes occur
	useEffect(() => {
		// Function to calculate counts
		const updateCounts = () => {
			// Find all checkboxes, radios in this group
			const container = containerRef.current;
			if (!container) return;
			
			// Count selected checkboxes
			const checkboxes = container.querySelectorAll('input[type="checkbox"]:checked');
			
			// Group radio buttons by name and count them as one selection per group
			const radioGroups = new Set();
			const selectedRadios = container.querySelectorAll('input[type="radio"]:checked');
			selectedRadios.forEach(radio => radioGroups.add(radio.name));
			
			// Count text inputs that have values (new feature)
			const textInputs = container.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], textarea');
			const filledTextInputsCount = Array.from(textInputs).filter(input => input.value.trim() !== '').length;
			filledTextInputs.current = filledTextInputsCount;
			
			// Total count is sum of checkboxes, unique radio groups, and filled text inputs
			const totalSelectedCount = checkboxes.length + radioGroups.size + filledTextInputsCount;
			
			// Update state
			setSelectedCount(totalSelectedCount);
			
			// Real-time validation
			const isCurrentlyValid = totalSelectedCount >= minCount && totalSelectedCount <= maxCount && (step.required ? totalSelectedCount > 0 : true);
			setInternalValidation(isCurrentlyValid);

			// Only show validation errors after user interaction
			if (userInteracted.current) {
				setShowRealTimeValidation(true);
			}
			
			return totalSelectedCount;
		};
		
		// Perform initial count
		updateCounts();
		
		// Set up event listeners
		const container = containerRef.current;
		if (container) {
			// Listen for changes to checkboxes, radios
			const handleSelectionChange = () => {
				userInteracted.current = true;
				updateCounts();
			};
			
			// Listen for input in text fields
			const handleTextInputChange = () => {
				userInteracted.current = true;
				updateCounts();
			};
			
			container.addEventListener('change', handleSelectionChange);
			container.addEventListener('input', handleTextInputChange);
			
			// Cleanup listeners
			return () => {
				container.removeEventListener('change', handleSelectionChange);
				container.removeEventListener('input', handleTextInputChange);
			};
		}
	}, [minCount, maxCount, step.required]);
	
	// Effect for validation and notification of parent component
	useEffect(() => {
		// Skip the initial render, only report when counts actually change
		if (isInitialRender.current) {
			isInitialRender.current = false;
			return;
		}
		
		// Check validity against min/max count
		const isValid = selectedCount >= minCount && selectedCount <= maxCount;
		
		// For radio groups, create a unique identifier based on step and group
		const radioGroup = `${stepNumber}`;
		
		// Send the updated count to the parent component if count changed
		if (prevSelectedCount.current !== selectedCount && onSelectionChange) {
			// Check if validation error should be cleared
			const clearValidationError = selectedCount >= minCount;
			
			onSelectionChange({
				stepNumber,
				selectedCount,
				minCount,
				maxCount,
				isValid,
				clearValidationError,
				maxReached: selectedCount >= maxCount,
				radioGroup,
				realTimeValidation: showRealTimeValidation && !isValid
			});
			
			// Update the previous count reference
			prevSelectedCount.current = selectedCount;
		}
	}, [selectedCount, minCount, maxCount, onSelectionChange, stepNumber, showRealTimeValidation]);

	// Generate validation error message if needed
	const validationMessage = () => {
		if (showValidationError && typeof showValidationError !== 'boolean') {
			return showValidationError;
		}

		if (!internalValidation){
			if (selectedCount > maxCount) {
				return `Maximal ${maxCount} ${maxCount === 1 ? 'Option' : 'Optionen'} auswählen.`;
			}
			if (selectedCount < minCount) {
				return `Mindestens ${minCount} ${minCount === 1 ? 'Option' : 'Optionen'} auswählen.`;
			}
			if (selectedCount === 0) {
				return `Bitte mindestens ${minCount} ${minCount === 1 ? 'Option' : 'Optionen'} auswählen.`;
			}

		}
	}


	// Get the name for the validation message using multiple sources
	const nameForValidation = step?.title || `Step ${stepNumber}`;
	
	// Create a unique identifier for the validation message

	return (
		<div 
			ref={containerRef} 
			className={`selection-counter-wrapper ${!internalValidation && showRealTimeValidation ? 'needs-validation' : ''}  ${showRealTimeValidation ? `${internalValidation ? '' : 'has-validation-error'}` : ''} `} 
			data-min-count={minCount}
			data-max-count={maxCount}
			data-step-number={stepNumber}
			data-validation-status={internalValidation ? 'valid' : 'invalid'}
			data-serial-key={createSerialKey(step, nameForValidation, `selection-counter-wrapper-${stepNumber}`)}
			>
			{children}
			
			{validationMessage() && showRealTimeValidation && (
				<div 
					className="invalid-feedback d-block mt-3"
					role="alert">
					{validationMessage()}
				</div>
			)}
		</div>
	);
};

export default SelectionCounter;
